import React, { useState, useEffect, useCallback } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  FlatList, 
  TouchableOpacity, 
  TextInput, 
  ActivityIndicator, 
  Alert,
  RefreshControl,
  ScrollView
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import { MainTabParamList } from '../../navigation/MainNavigator';
import { Exercise } from '../../types';
import { MUSCLE_GROUPS } from '../../constants/muscleGroups';
import { typography, spacing, borderRadius } from '../../constants/theme';
import { useTheme } from '../../context/ThemeContext';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import { CompositeScreenProps } from '@react-navigation/native';
import { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useFocusEffect } from '@react-navigation/native';

type Props = CompositeScreenProps<
  BottomTabScreenProps<MainTabParamList, 'Exercises'>,
  NativeStackScreenProps<MainStackParamList>
>;

const ExerciseLibraryScreen = ({ navigation }: Props) => {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [filteredExercises, setFilteredExercises] = useState<Exercise[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [headerRefreshing, setHeaderRefreshing] = useState(false);
  const { theme } = useTheme();

  useFocusEffect(
    useCallback(() => {
      fetchExercises();
    }, [])
  );

  useEffect(() => {
    filterExercises();
  }, [exercises, searchQuery, selectedMuscleGroup]);

  const fetchExercises = async () => {
    if (!refreshing && !headerRefreshing) setLoading(true);
    try {
      const exercisesCollection = collection(firestore, 'exercises');
      const exercisesQuery = query(exercisesCollection, orderBy('name'));
      const querySnapshot = await getDocs(exercisesQuery);
      
      const exercisesData: Exercise[] = [];
      querySnapshot.forEach((doc) => {
        exercisesData.push({ id: doc.id, ...doc.data() } as Exercise);
      });
      
      setExercises(exercisesData);
    } catch (error) {
      console.error('Error al obtener ejercicios:', error);
      Alert.alert('Error', 'No se pudieron cargar los ejercicios');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setHeaderRefreshing(false);
    }
  };

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchExercises();
  }, []);

  const onHeaderRefresh = useCallback(() => {
    setHeaderRefreshing(true);
    fetchExercises();
  }, []);

  const filterExercises = () => {
    let filtered = [...exercises];
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(exercise => 
        exercise.name.toLowerCase().includes(query) || 
        exercise.description.toLowerCase().includes(query)
      );
    }
    
    // Filter by muscle group
    if (selectedMuscleGroup) {
      filtered = filtered.filter(exercise => 
        exercise.muscleGroups.includes(selectedMuscleGroup)
      );
    }
    
    setFilteredExercises(filtered);
  };

  const handleMuscleGroupPress = (muscleGroupId: string) => {
    setSelectedMuscleGroup(currentSelected => 
      currentSelected === muscleGroupId ? null : muscleGroupId
    );
  };

  const handleExercisePress = (exerciseId: string) => {
    navigation.navigate('ExerciseLibraryDetail', { exerciseId });
  };

  const renderMuscleGroupItem = ({ item }: { item: typeof MUSCLE_GROUPS[0] }) => {
    const isSelected = selectedMuscleGroup === item.id;
    
    return (
      <TouchableOpacity
        style={[
          styles.muscleGroupItem,
          theme.shadows.light,
          { backgroundColor: theme.colors.card },
          isSelected && { backgroundColor: theme.colors.primary }
        ]}
        onPress={() => handleMuscleGroupPress(item.id)}
      >
        <Ionicons 
          name={item.icon as any}
          size={24} 
          color={isSelected ? theme.colors.white : theme.colors.primary} 
        />
        <Text 
          style={[
            styles.muscleGroupText,
            { color: theme.colors.text },
            isSelected && { color: theme.colors.white }
          ]}
        >
          {item.name}
        </Text>
      </TouchableOpacity>
    );
  };

  const renderExerciseItem = ({ item }: { item: Exercise }) => {
    return (
      <Card 
        style={styles.exerciseCard}
        onPress={() => handleExercisePress(item.id)}
      >
        <Text style={[styles.exerciseName, { color: theme.colors.text }]}>{item.name}</Text>
        <Text style={[styles.exerciseDescription, { color: theme.colors.grey.dark }]} numberOfLines={2}>
          {item.description}
        </Text>
        
        <View style={styles.exerciseTags}>
          {item.muscleGroups.map(groupId => {
            const muscleGroup = MUSCLE_GROUPS.find(group => group.id === groupId);
            return muscleGroup ? (
              <View key={groupId} style={[styles.exerciseTag, { backgroundColor: theme.colors.grey.light }]}>
                <Text style={[styles.exerciseTagText, { color: theme.colors.grey.dark }]}>{muscleGroup.name}</Text>
              </View>
            ) : null;
          })}
        </View>
      </Card>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header con Pull to Refresh */}
      <ScrollView
        style={styles.headerScrollView}
        contentContainerStyle={styles.headerScrollViewContent}
        refreshControl={
          <RefreshControl
            refreshing={headerRefreshing}
            onRefresh={onHeaderRefresh}
            colors={[theme.colors.primary]}
            tintColor={theme.colors.primary}
            progressBackgroundColor={theme.colors.card}
          />
        }
        scrollEnabled={true}
      >
        {/* Search Bar */}
        <View style={[styles.searchContainer, { backgroundColor: theme.colors.card }, theme.shadows.light]}>
          <Ionicons name="search" size={20} color={theme.colors.grey.medium} />
          <TextInput
            style={[styles.searchInput, { color: theme.colors.text }]}
            placeholder="Buscar ejercicios"
            placeholderTextColor={theme.colors.grey.medium}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color={theme.colors.grey.medium} />
            </TouchableOpacity>
          )}
        </View>

        {/* Muscle Groups Filter */}
        <View style={styles.muscleGroupsContainer}>
          <FlatList
            data={MUSCLE_GROUPS}
            renderItem={renderMuscleGroupItem}
            keyExtractor={item => item.id}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.muscleGroupsList}
          />
        </View>

        {/* Create Exercise Button */}
        <TouchableOpacity 
          style={[styles.createExerciseButton, { backgroundColor: theme.colors.primary }, theme.shadows.light]}
          onPress={() => navigation.navigate('CreateExercise')}
        >
          <Ionicons name="add" size={24} color={theme.colors.white} />
          <Text style={[styles.createExerciseText, { color: theme.colors.white }]}>Crear Ejercicio</Text>
        </TouchableOpacity>
      
        {/* Pull to refresh hint when headerRefreshing is true */}
        {headerRefreshing && (
          <Text style={[styles.refreshHint, { color: theme.colors.grey.medium }]}>
            Desliza hacia abajo para refrescar...
          </Text>
        )}
      </ScrollView>

      {/* Exercises List */}
      {loading && !refreshing && !headerRefreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : filteredExercises.length > 0 ? (
        <FlatList
          data={filteredExercises}
          renderItem={renderExerciseItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.exercisesList}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[theme.colors.primary]}
              tintColor={theme.colors.primary}
              progressBackgroundColor={theme.colors.card}
            />
          }
        />
      ) : (
        <View style={styles.emptyContainer}>
          <Ionicons name="barbell-outline" size={64} color={theme.colors.grey.medium} />
          <Text style={[styles.emptyText, { color: theme.colors.grey.dark }]}>No se encontraron ejercicios</Text>
          {searchQuery.length > 0 || selectedMuscleGroup ? (
            <TouchableOpacity 
              style={[styles.clearFiltersButton, { borderColor: theme.colors.primary }]}
              onPress={() => {
                setSearchQuery('');
                setSelectedMuscleGroup(null);
              }}
            >
              <Text style={[styles.clearFiltersText, { color: theme.colors.primary }]}>Limpiar filtros</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity 
              style={[styles.refreshButton, { backgroundColor: theme.colors.primary }]}
              onPress={fetchExercises}
            >
              <Text style={[styles.refreshText, { color: theme.colors.white }]}>Recargar</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerScrollView: {
    maxHeight: 220,
    zIndex: 10,
  },
  headerScrollViewContent: {
    padding: spacing.md,
  },
  refreshHint: {
    textAlign: 'center',
    fontSize: typography.fontSize.sm,
    marginVertical: spacing.xs,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
    height: 46,
  },
  searchInput: {
    flex: 1,
    marginLeft: spacing.sm,
    fontSize: typography.fontSize.md,
  },
  muscleGroupsContainer: {
    marginBottom: spacing.md,
  },
  muscleGroupsList: {
    paddingVertical: spacing.xs,
  },
  muscleGroupItem: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginRight: spacing.sm,
  },
  muscleGroupText: {
    marginLeft: spacing.xs,
    fontSize: typography.fontSize.sm,
  },
  createExerciseButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: borderRadius.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  createExerciseText: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
    marginLeft: spacing.xs,
  },
  exercisesList: {
    padding: spacing.md,
    paddingTop: 0,
  },
  exerciseCard: {
    marginBottom: spacing.md,
  },
  exerciseName: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginBottom: spacing.xs,
  },
  exerciseDescription: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
  },
  exerciseTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  exerciseTag: {
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    marginRight: spacing.xs,
    marginBottom: spacing.xs,
  },
  exerciseTagText: {
    fontSize: typography.fontSize.xs,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: typography.fontSize.lg,
    marginTop: spacing.md,
    marginBottom: spacing.lg,
  },
  clearFiltersButton: {
    borderWidth: 1,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  clearFiltersText: {
    fontSize: typography.fontSize.md,
  },
  refreshButton: {
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  refreshText: {
    fontSize: typography.fontSize.md,
  },
});

export default ExerciseLibraryScreen; 